import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { supabase } from '../../config/supabase';

export interface UserProfileBasicInfo {
  firstName?: string;
  lastName?: string;
}

export interface UserPreferencesState {
  preferences: UserProfileBasicInfo | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: UserPreferencesState = {
  preferences: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

// Async thunk for fetching user preferences
export const fetchUserPreferences = createAsyncThunk(
  'userPreferences/fetch',
  async (userId: string, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', userId)
        .single();

      if (error) {
        return rejectWithValue(error.message);
      }

      return {
        firstName: data.first_name,
        lastName: data.last_name,
      } as UserProfileBasicInfo;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to load preferences'
      );
    }
  }
);

// Async thunk for updating user preferences
export const updateUserPreferences = createAsyncThunk(
  'userPreferences/update',
  async (
    {
      userId,
      preferences,
    }: { userId: string; preferences: Partial<UserProfileBasicInfo> },
    { rejectWithValue }
  ) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          first_name: preferences.firstName,
          last_name: preferences.lastName,
        })
        .eq('id', userId)
        .select('first_name, last_name')
        .single();

      if (error) {
        return rejectWithValue(error.message);
      }

      return {
        firstName: data.first_name,
        lastName: data.last_name,
      } as UserProfileBasicInfo;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update preferences'
      );
    }
  }
);

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    clearPreferences: state => {
      state.preferences = null;
      state.error = null;
      state.lastFetched = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch user preferences
      .addCase(fetchUserPreferences.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update user preferences
      .addCase(updateUserPreferences.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPreferences, setError } = userPreferencesSlice.actions;
export default userPreferencesSlice.reducer;

// Selectors
export const selectUserPreferences = (state: {
  userPreferences: UserPreferencesState;
}) => state.userPreferences.preferences;

export const selectUserPreferencesLoading = (state: {
  userPreferences: UserPreferencesState;
}) => state.userPreferences.isLoading;

export const selectUserPreferencesError = (state: {
  userPreferences: UserPreferencesState;
}) => state.userPreferences.error;

export const selectUserPreferencesLastFetched = (state: {
  userPreferences: UserPreferencesState;
}) => state.userPreferences.lastFetched;
